import { mapGetters } from 'vuex'
import { payload as accountActionPayload } from '@/components/RouteComponents/Accounts/actionPayload'
import { payload as seriesActionPayload } from '@/components/RouteComponents/Series/actionPayload'
import { payload as locationActionPayload } from '@/components/RouteComponents/Locations/actionPayload'
import { payload as setActionPayload } from '@/components/RouteComponents/Sets/actionPayload'
import { payload as freefieldActionPayload } from '@/components/RouteComponents/Freefields/actionPayload'
import { payload as billableitemActionPayload } from '@/components/RouteComponents/Billableitems/actionPayload'
import AutocompleteLocalSearch from '@/utils/helpers/Search/AutocompleteLocalSearch'

export const arMixins = {
  computed: {
    ...mapGetters({
      getCustomerById: 'getCustomerById',
      getAccountById: 'getAccountById',
      currentPeriod: 'getCurrentPeriod',
      getBankByBatchId: 'getBankByBatchId'
    }),
    /**
     * Calculates the header amount with markups
     * @return {string}
     */
    amountWithMarkups () {
      if (this.lines && this.lines.length) {
        let amt = this.lines.reduce((a, b) => {
          const withMarkups = b.form.amount_with_markups_decimal ? parseFloat(b.form.amount_with_markups_decimal) : 0
          return a + parseFloat(withMarkups)
        }, 0)
        return (amt && !isNaN(amt) && typeof amt === 'number') ? amt.toFixed(2) : amt
      }
      return ''
    },
    // TODO: possibly duplicated in Transactions.js mixin
    currentCompany () {
      const { getCompanyByBatchId } = this.$store.getters
      if (this.form.batch && this.form.batch.value && getCompanyByBatchId(this.form.batch.value)) {
        return getCompanyByBatchId(this.form.batch.value).code
      }
      return ''
    },
    // TODO: possibly duplicated in Transactions.js mixin
    currentProduction () {
      const { getProductionByBatchId } = this.$store.getters
      if (this.form.batch && this.form.batch.value && getProductionByBatchId(this.form.batch.value)) {
        return getProductionByBatchId(this.form.batch.value).code
      }
      return ''
    },
    /**
     * Returns the line fields for this transaction type.
     * Note: this overrides the `lineFields` computed property set in the base Transactions mixin at @/Mixins/Transactions.js
     * @return Array
     */
    lineFields () {
      return [
        {
          name: 'line_number',
          label: 'Line',
          dataType: 'number',
          fieldType: 'input',
          disabled: true,
          fieldWidth: '2%'
        },
        {
          name: 'company',
          label: 'Co',
          dataType: 'string',
          fieldType: 'display',
          displayVal: this.currentCompany,
          fieldWidth: '3.5%',
          hidden: true
        },
        {
          name: 'production',
          label: 'Prod',
          dataType: 'string',
          fieldType: 'display',
          displayVal: this.currentProduction,
          fieldWidth: '3.5%',
          hidden: true
        },
        {
          name: 'account',
          label: 'Account',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.postableAccounts, accountActionPayload).createOptions(),
          remote: false,
          required: true,
          fieldWidth: '5%',
          readOnly: this.readOnly,
          actionPayload: accountActionPayload
        },
        {
          name: 'account:name',
          label: 'Account Name',
          dataType: 'string',
          fieldType: 'display',
          displayVal: (accountId) => {
            const acct = this.$store.getters.getAccountById(accountId)
            return acct && acct.id ? acct.name : ''
          },
          fieldWidth: '8%',
          hidden: false
        },
        {
          name: 'item',
          label: 'Item',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.billableitems, billableitemActionPayload).createOptions(),
          remote: false,
          required: true,
          fieldWidth: '6%',
          readOnly: this.readOnly,
          actionPayload: billableitemActionPayload
        },
        {
          name: 'description',
          label: 'Description',
          dataType: 'string',
          fieldType: 'input',
          required: true,
          fieldWidth: '20%',
          readOnly: this.readOnly
        },
        {
          name: 'amount_decimal',
          label: 'Amount',
          dataType: 'number',
          fieldType: 'money',
          required: true,
          fieldWidth: '8%',
          readOnly: this.readOnly
        },
        {
          name: 'markup_type',
          label: 'Markup Type',
          dataType: 'string',
          fieldType: 'select',
          required: true,
          options: [
            { label: 'Percentage', value: 'PCT' },
            { label: 'Amount', value: 'AMT' },
            { label: 'Not Set', value: 'NON' }
          ],
          fieldWidth: '7%',
          readOnly: this.readOnly
        },
        {
          name: 'markup_amount',
          label: 'Markup Amount',
          dataType: 'number',
          fieldType: 'money',
          required: false,
          fieldWidth: '5%',
          readOnly: this.readOnly
        },
        {
          name: 'amount_with_markups_decimal',
          label: 'Amt w/ Markups',
          dataType: 'number',
          fieldType: 'money',
          required: false,
          readOnly: true,
          disabled: true,
          fieldWidth: '8%'
        },
        {
          name: 'series',
          label: 'Series',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.series, seriesActionPayload).createOptions(),
          remote: false,
          required: true,
          readOnly: this.readOnly,
          fieldWidth: '5%',
          actionPayload: seriesActionPayload
        },
        {
          name: 'location',
          label: 'Location',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.locations, locationActionPayload).createOptions(),
          remote: false,
          required: true,
          readOnly: this.readOnly,
          fieldWidth: '5%',
          actionPayload: locationActionPayload
        },
        {
          name: 'set',
          label: 'Set',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.sets, setActionPayload).createOptions(),
          remote: false,
          required: true,
          readOnly: this.readOnly,
          fieldWidth: '5%',
          actionPayload: setActionPayload
        },
        {
          name: 'free_field_1',
          label: 'FF 1',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.freefields, freefieldActionPayload).createOptions(),
          remote: false,
          readOnly: this.readOnly,
          fieldWidth: '5%',
          actionPayload: freefieldActionPayload
        },
        {
          name: 'free_field_2',
          label: 'FF 2',
          dataType: 'string',
          fieldType: 'autocomplete',
          options: () => new AutocompleteLocalSearch(this.freefields, freefieldActionPayload).createOptions(),
          remote: false,
          readOnly: this.readOnly,
          fieldWidth: '5%',
          actionPayload: freefieldActionPayload
        }
      ]
    }
  }
}



// WEBPACK FOOTER //
// ./src/Mixins/ArMixins.js